import React, { ComponentType, useState } from "react"
import { compose } from "@reduxjs/toolkit"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { DialogContent } from "@mui/material"

import i18n from "config/i18n"
import { useAppDispatch } from "redux/hooks"
import {
  withPaymentMethods,
  withPlans,
  withModalSubscriptionId,
  withModalSubscriptionPlan,
  withModalStep,
} from "hocs/features"
import { showAlert } from "redux/features/notice/noticeSlice"
import {
  closeDialog,
  updateDialogStep,
  updateDialog,
} from "redux/features/dialog/dialogSlice"
import PollingService from "redux/api/pollingService"
import {
  makeSubscription,
  makeUpdateSubscription,
} from "redux/features/subscription/subscriptionThunk"
import { makeStripeUriRequest } from "redux/features/stripe_uri_request/stripeUriRequestThunk"
import {
  makeZlickAuthRequest,
  makeFinishZlickAuthRequest,
} from "redux/features/zlick_auth_request/zlickAuthRequestThunk"
import {
  StepperComponent as Stepper,
  StepComponent as Step,
  StepContentComponent as StepContent,
} from "components/ui/Stepper"
import Spinner from "components/ui/Spinner"
import Back from "components/ui/ModalBack"
import Close from "components/ui/ModalClose"
import Plans from "components/Subscription/Plans/List"
import {
  Container,
  Content,
  Title,
  Description,
} from "components/Subscription/Modal/Create/elements"
import Providers from "components/Subscription/Modal/Create/Providers"
import BankPayment from "components/Subscription/Modal/Create/BankPayment"
import MobilePayment from "components/Subscription/Modal/Create/MobilePayment"
import MobilePaymentConfirmation from "components/Subscription/Modal/Create/MobilePaymentConfirmation"
import {
  SUBSCRIPTION_CREATE_STEP_INITIAL,
  SUBSCRIPTION_CREATE_STEP_PROVIDERS,
  SUBSCRIPTION_CREATE_STEP_MOBILE_PAYMENT,
  SUBSCRIPTION_CREATE_STEP_MOBILE_PAYMENT_CONFIRMATION,
  SUBSCRIPTION_CREATE_STEP_BANK_PAYMENT,
  SUBSCRIPTION_CREATE_STEP_PAYMENT_FAILED,
  SUBSCRIPTION_BANK_METHOD,
  SUBSCRIPTION_MOBILE_METHOD,
  SUBSCRIPTION_CARD_METHOD,
  SUBSCRIPTION_SUCCESS_MATCH,
  SUBSCRIPTION_CREATE_TYPE,
  CLIENT_SUBSCRIPTIONS_MATCH,
} from "config"
import {
  PlanListType,
  PlanOptionType,
  SubscriptionPaymentMethodList,
  SubscriptionPhoneConfirmationValues,
  SubscriptionValues,
  ZlickAuthRequestValues,
} from "types"

type Props = {
  plans: PlanListType
  paymentMethods: SubscriptionPaymentMethodList
  subscriptionId: string
  plan: PlanOptionType | null
  step: number
}

const SubscriptionCreateModal = ({
  plans,
  paymentMethods,
  subscriptionId,
  plan,
  step = SUBSCRIPTION_CREATE_STEP_INITIAL,
}: Props) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [userAuthRequestId, setUserAuthRequestId] = useState("")
  const [isSubmitting, setSubmitting] = useState(false)

  const isBackHidden = [
    SUBSCRIPTION_CREATE_STEP_INITIAL,
    SUBSCRIPTION_CREATE_STEP_PAYMENT_FAILED,
  ].includes(step)

  const handleBack = () => {
    switch (step) {
      case SUBSCRIPTION_CREATE_STEP_PROVIDERS:
        dispatch(updateDialogStep({ step: SUBSCRIPTION_CREATE_STEP_INITIAL }))
        return
      case SUBSCRIPTION_CREATE_STEP_MOBILE_PAYMENT_CONFIRMATION:
        dispatch(
          updateDialogStep({ step: SUBSCRIPTION_CREATE_STEP_MOBILE_PAYMENT }),
        )
        return
      case SUBSCRIPTION_CREATE_STEP_BANK_PAYMENT:
        dispatch(updateDialogStep({ step: SUBSCRIPTION_CREATE_STEP_PROVIDERS }))
        return
      default:
        return
    }
  }

  const handleClose = () => {
    if (PollingService.isPolling()) {
      return dispatch(
        showAlert({
          message: i18n.t("common.request_in_progress"),
        }),
      )
    }
    dispatch(closeDialog())
    navigate(CLIENT_SUBSCRIPTIONS_MATCH)
  }

  const handleChoosePlan = (plan: PlanOptionType) => {
    dispatch(
      updateDialog({
        content: {
          plan: plan,
        },
      }),
    )
    dispatch(updateDialogStep({ step: SUBSCRIPTION_CREATE_STEP_PROVIDERS }))
  }

  const handleZlickAuthRequest = (values: ZlickAuthRequestValues) => {
    const { subscription_id, phone } = values

    dispatch(
      makeZlickAuthRequest({
        subscription_id,
        phone,
      }),
    )
      .then(({ id }) => {
        setUserAuthRequestId(id)
        dispatch(
          updateDialogStep({
            step: SUBSCRIPTION_CREATE_STEP_MOBILE_PAYMENT_CONFIRMATION,
          }),
        )
      })
      .catch((error) => {
        dispatch(showAlert({ message: error }))
      })
  }

  const finishZlickAuthRequest = (
    values: SubscriptionPhoneConfirmationValues,
  ) => {
    setSubmitting(true)

    dispatch(makeFinishZlickAuthRequest(values))
      .then(() => {
        updateMobileSubscriptionRequest(values)
      })
      .catch((error) => {
        dispatch(showAlert({ message: error }))
        dispatch(
          updateDialogStep({ step: SUBSCRIPTION_CREATE_STEP_PAYMENT_FAILED }),
        )
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  const processSubscriptionCreate = (id: string, service_type: string) => {
    dispatch(updateDialog({ content: { subscriptionId: id } }))

    const isMobile = service_type === SUBSCRIPTION_MOBILE_METHOD
    const isCard = service_type === SUBSCRIPTION_CARD_METHOD
    const isBank = service_type === SUBSCRIPTION_BANK_METHOD

    if (isMobile) {
      dispatch(
        updateDialogStep({ step: SUBSCRIPTION_CREATE_STEP_MOBILE_PAYMENT }),
      )
    } else if (isCard) {
      dispatch(makeStripeUriRequest({ subscription_id: id }))
        .then(({ redirect_url }) => {
          window.location.href = redirect_url
        })
        .catch((error) => {
          dispatch(showAlert({ message: error }))
        })
    } else if (isBank) {
      dispatch(
        updateDialogStep({
          step: SUBSCRIPTION_CREATE_STEP_BANK_PAYMENT,
        }),
      )
    }
  }

  const handleSubmit = (values: SubscriptionValues) => {
    setSubmitting(true)

    const { service_type, plan_id } = values

    dispatch(
      makeSubscription({
        plan_id,
        service_type,
      }),
    )
      .then(({ id }) => {
        processSubscriptionCreate(id, service_type)
      })
      .catch((error) => {
        dispatch(showAlert({ message: error }))
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  const updateMobileSubscriptionRequest = (
    values: SubscriptionPhoneConfirmationValues,
  ) => {
    setSubmitting(true)

    const params = {
      action: "zlick_create_subscription",
      user_auth_request_id: values.user_auth_request_id,
      subscription_id: values.subscription_id,
    }

    dispatch(makeUpdateSubscription(params))
      .then(() => {
        navigate(SUBSCRIPTION_SUCCESS_MATCH)
      })
      .catch((error) => {
        dispatch(showAlert({ message: error }))
        dispatch(
          updateDialogStep({ step: SUBSCRIPTION_CREATE_STEP_PAYMENT_FAILED }),
        )
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  const STEPS = [
    {
      label: SUBSCRIPTION_CREATE_STEP_INITIAL,
      component: (
        <Content>
          <Title variant="h4">
            {t("features.subscription.steps.plan.title")}
          </Title>
          <Description variant="h6">
            {t("features.subscription.steps.plan.description")}
          </Description>
          <Plans
            items={plans}
            subscriptionType={SUBSCRIPTION_CREATE_TYPE}
            onChoose={handleChoosePlan}
          />
        </Content>
      ),
    },
    {
      label: SUBSCRIPTION_CREATE_STEP_PROVIDERS,
      component: (
        <>
          {plan && (
            <Providers failure={false} plan={plan} onSubmit={handleSubmit} />
          )}
        </>
      ),
    },
    {
      label: SUBSCRIPTION_CREATE_STEP_MOBILE_PAYMENT,
      component: (
        <MobilePayment
          subscription_id={subscriptionId}
          onSubmit={handleZlickAuthRequest}
        />
      ),
    },
    {
      label: SUBSCRIPTION_CREATE_STEP_MOBILE_PAYMENT_CONFIRMATION,
      component: (
        <MobilePaymentConfirmation
          subscription_id={subscriptionId}
          user_auth_request_id={userAuthRequestId}
          onSubmit={finishZlickAuthRequest}
        />
      ),
    },
    {
      label: SUBSCRIPTION_CREATE_STEP_BANK_PAYMENT,
      component: (
        <BankPayment
          subscriptionId={subscriptionId}
          paymentMethods={paymentMethods}
        />
      ),
    },
    {
      label: SUBSCRIPTION_CREATE_STEP_PAYMENT_FAILED,
      component: (
        <>
          {plan && (
            <Providers failure={true} plan={plan} onSubmit={handleSubmit} />
          )}
        </>
      ),
    },
  ]

  return (
    <DialogContent>
      <Spinner open={isSubmitting} />
      <Back
        disabled={isSubmitting}
        hidden={isBackHidden}
        onClick={handleBack}
      />
      <Close onClick={handleClose} />
      <Container
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
      >
        <Stepper activeStep={step} orientation="vertical">
          {STEPS.map(({ component }, index: number) => (
            <Step key={index}>
              <StepContent>{component}</StepContent>
            </Step>
          ))}
        </Stepper>
      </Container>
    </DialogContent>
  )
}

export default compose<ComponentType>(
  withModalStep,
  withModalSubscriptionId,
  withModalSubscriptionPlan,
  withPlans,
  withPaymentMethods,
)(SubscriptionCreateModal)
